import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardData, deleteAnalysis } from '../api/BackendApi.jsx';
import { Loader, AlertTriangle, BarChart2, FileText, ChevronRight, CreditCard, ChevronLeft, Trash2, Search, Calendar } from 'lucide-react';

const DashboardPage = ({updateBalance}) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ credits: 0, finished: [], started: [], paid: [] });
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [error, setError] = useState('');
  const [finishedPage, setFinishedPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const [searchQuestion, setSearchQuestion] = useState('');
  const [searchDate, setSearchDate] = useState('');
  // Reset stránkování při změně vyhledávání, abyste neuvízli na prázdné stránce
  useEffect(() => {
    setFinishedPage(1);
  }, [searchQuestion, searchDate]);

  // --- STAVY PRO POTVRZOVACÍ DIALOG MAZÁNÍ ---
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getDashboardData();
        setData({
          credits: response.credit || 0,
          finished: response.finished || [],
          ongoing: response.ongoing || [],
        });
        updateBalance(response.credit);
        setStatus('success');
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setError(err.message || 'Nepodařilo se načíst data.');
        setStatus('error');
      }
    };
    fetchData();
  }, [updateBalance]);

  const handleOpenAnalysis = (analysis) => {
    if (analysis.status === 'finished' || analysis.status === 'started' || analysis.status === 'error') {
      navigate(`/analysis/${analysis.id}/summary`)
    } else if (analysis.status === 'suggested' || analysis.status === 'suggesting') {
      navigate(`/analysis/create?step=${4}&fileId=${analysis.id}`)
    } else {
      navigate(`/analysis/create?step=${3}&fileId=${analysis.id}`)
    }
  };

  // --- LOGIKA PRO MAZÁNÍ ---
  const handleDeleteClick = (id) => {
    setAnalysisToDelete(id);
    setDeleteModalOpen(true);
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setAnalysisToDelete(null);
  };

  const confirmDelete = async () => {
    if (!analysisToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteAnalysis(analysisToDelete);
      
      // Aktualizace stavu - odstranění smazané analýzy ze seznamů
      setData((prevData) => ({
        ...prevData,
        finished: prevData.finished.filter((analysis) => analysis.id !== analysisToDelete),
        ongoing: prevData.ongoing.filter((analysis) => analysis.id !== analysisToDelete),
      }));
      
      setDeleteModalOpen(false);
      setAnalysisToDelete(null);
    } catch (err) {
      console.error("Failed to delete analysis:", err);
      alert(err.message || 'Nepodařilo se smazat analýzu.');
    } finally {
      setIsDeleting(false);
    }
  };
  // -------------------------

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('cs-CZ', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
  };

  const renderAnalysisSection = (title, analysesList, isFinished, pagination = null) => {
    let displayList = analysesList;
    let totalPages = 0;
    if (pagination) {
        const { currentPage, itemsPerPage } = pagination;
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        displayList = analysesList.slice(indexOfFirstItem, indexOfLastItem);
        totalPages = Math.ceil(analysesList.length / itemsPerPage);
    }

    return (
      <div className="mt-10">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{title}</h2>
        {analysesList.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {displayList.map((analysis) => (
                <li key={analysis.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center">
                    <div className="p-3 bg-green-100 rounded-full mr-4">
                      <FileText className="w-5 h-5 text-green-700" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Vytvořeno: {formatDate(analysis.date)}
                      </p>
                      {analysis.answer_count !== undefined && (
                        <p className="text-sm text-gray-500">
                          Počet odpovědí: {analysis.answer_count}
                        </p>
                      )}
                      <p className="text-base font-semibold text-gray-800 truncate max-w-xs" title={analysis.question}>
                        {analysis.question || 'Analýza bez názvu'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleDeleteClick(analysis.id)}
                      className="p-2 text-red-500 bg-red-50 rounded-full hover:bg-red-100 hover:text-red-700 transition-colors"
                      title="Smazat analýzu"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => handleOpenAnalysis(analysis)}
                      className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                    >
                      Zobrazit
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination Controls */}
            {pagination && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-1 justify-between sm:hidden">
                    {/* Mobile simpler view */}
                   <button
                    onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Předchozí
                  </button>
                  <button
                    onClick={() => pagination.onPageChange(Math.min(totalPages, pagination.currentPage + 1))}
                    disabled={pagination.currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Další
                  </button>
                </div>
                
                {/* Desktop view */}
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Zobrazeno <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> až <span className="font-medium">{Math.min(pagination.currentPage * pagination.itemsPerPage, analysesList.length)}</span> z <span className="font-medium">{analysesList.length}</span> výsledků
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
                        disabled={pagination.currentPage === 1}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                      </button>
                      
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                        {pagination.currentPage} / {totalPages}
                      </span>

                      <button
                        onClick={() => pagination.onPageChange(Math.min(totalPages, pagination.currentPage + 1))}
                        disabled={pagination.currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
            <BarChart2 className="w-12 h-12 text-gray-300 mb-2" />
            <h3 className="text-lg font-semibold text-gray-700">Žádné položky</h3>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center h-64 bg-red-50 text-red-700 p-6 rounded-lg">
          <AlertTriangle className="w-12 h-12 mb-4" />
          <h3 className="text-xl font-semibold">Chyba při načítání</h3>
          <p>{error}</p>
        </div>
      );
    }

    const { credits, ongoing, finished } = data;

    // Logika filtrování podle textu a data 
    const lowerQuestion = searchQuestion.toLowerCase();
    // Convert to lowercase AND remove all spaces from the search input
    const normalizedSearchDate = searchDate.toLowerCase().replace(/\s+/g, '');

    const filterFn = (analysis) => {
      let matchesQuestion = true;
      let matchesDate = true;

      // Kontrola otázky (pokud je pole vyplněno)
      if (searchQuestion) {
        matchesQuestion = analysis.question && analysis.question.toLowerCase().includes(lowerQuestion);
      }
      
      // Kontrola data (pokud je pole vyplněno)
      if (searchDate) {
        const normalizedFormattedDate = formatDate(analysis.date).toLowerCase().replace(/\s+/g, '');
        matchesDate = normalizedFormattedDate.includes(normalizedSearchDate);
      }
      
      // Musí splňovat obě podmínky (AND logika)
      return matchesQuestion && matchesDate;
    };

    const filteredOngoing = ongoing.filter(filterFn);
    const filteredFinished = finished.filter(filterFn);

    return (
      <>
        {/* Credits */}
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-500">Dostupný kredit</span>
            <p className="text-3xl font-bold text-gray-800">{credits} Kč</p>
          </div>
          <div className="p-4 bg-blue-100 rounded-full">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        {/* --- Vyhledávací pole --- */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          {/* Hledání podle otázky */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
              placeholder="Hledat podle otázky (např. 'Které značky nádobí...')"
              value={searchQuestion}
              onChange={(e) => setSearchQuestion(e.target.value)}
            />
          </div>

          {/* Hledání podle data */}
          <div className="relative sm:w-1/3 min-w-[250px]">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors shadow-sm"
              placeholder="Hledat podle data"
              value={searchDate}
              onChange={(e) => setSearchDate(e.target.value)}
            />
          </div>
        </div>
        {/* ---------------------------------- */}

        {/* 1. Not finished */}
        {renderAnalysisSection(
          'Nedokončené analýzy', 
          filteredOngoing, 
          false,
        )}

        {/* 2. Finished Analyses */}
        {renderAnalysisSection(
          'Dokončené analýzy', 
          filteredFinished, 
          true,
          { 
            currentPage: finishedPage, 
            itemsPerPage: ITEMS_PER_PAGE, 
            onPageChange: setFinishedPage 
          }
        )}
      </>
    );
  };

  return (
    <div className="w-full h-full bg-gray-50 relative">
      <div className="container mx-auto max-w-4xl py-12 px-6">
        {renderContent()}
      </div>

      {/* --- MODÁLNÍ OKNO PRO POTVRZENÍ SMAZÁNÍ --- */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-red-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-800 mb-2">Opravdu chcete smazat tuto analýzu?</h3>
            
            <p className="text-gray-600 mb-6">
              Tato akce je nevratná. Všechna data spojená s touto analýzou budou trvale smazána.
            </p>

            <div className="flex gap-3 w-full">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="w-1/2 bg-white border border-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Zrušit
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="w-1/2 bg-red-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Probíhá odstranění...
                  </>
                ) : (
                  'Ano, smazat'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardPage;